import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Building2, MapPin, Phone, ArrowLeft, Store, ShoppingCart, Plus, Minus, Navigation, Share2, LogIn, MessageCircle, Gift, Star, Images } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import UpiPaymentDialog from "@/components/UpiPaymentDialog";

const mapsUrl = (p) => {
  const q = [p.business_name, p.address, p.city, p.state].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};
const cleanPhone = (v) => (v || "").replace(/[^\d]/g, "");
const waUrl = (p) => {
  const n = cleanPhone(p.whatsapp_no || p.phone);
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(`Hi ${p.business_name}, I found your shop on METHOO STORE`)}` : null;
};

export default function PartnerShopPage() {
  const { partnerCode } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const placeholder = settings?.product_placeholder_image_url_full;
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [paymentProfile, setPaymentProfile] = useState(null);
  const [err, setErr] = useState(null);
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [guestMemberRef, setGuestMemberRef] = useState("");
  const [cashback, setCashback] = useState(null); // {percent, max, eligible}

  useEffect(() => {
    api.get(`/directory/partner/${partnerCode}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Shop not found"));
  }, [partnerCode]);

  useEffect(() => {
    api.get(`/partner/public-payment-profile/${partnerCode}`)
      .then((r) => setPaymentProfile(r.data))
      .catch(() => setPaymentProfile(null));
  }, [partnerCode]);

  // Load cashback offer (public setting) + user's eligibility (only if logged in as member)
  useEffect(() => {
    api.get("/settings").then(r => {
      const pct = Number(r.data?.first_partner_order_cashback_percent) || 0;
      const max = Number(r.data?.first_partner_order_cashback_max) || 0;
      if (pct > 0) setCashback(c => ({ ...(c || {}), percent: pct, max }));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user || user.role !== "member") return;
    api.get("/auth/me").then(r => {
      setCashback(c => ({ ...(c || {}), eligible: !r.data?.first_partner_cashback_credited }));
    }).catch(() => {});
  }, [user]);

  const p = data?.partner;
  const products = useMemo(() => data?.products || [], [data?.products]);
  const featuredProducts = useMemo(() => products.slice(0, 5), [products]);
  const featuredImages = useMemo(() => Array.isArray(data?.featured_images) ? data.featured_images.filter(Boolean).slice(0, 5) : [], [data?.featured_images]);
  const getStock = (product) => Math.max(0, Number(product?.stock ?? 0));

  const inc = (id) => {
    const product = products.find((x) => x.id === id);
    const stock = getStock(product);
    if (stock <= 0) {
      toast.error(`${product?.name || "Product"}: out of stock`);
      return;
    }
    const current = Number(cart[id] || 0);
    if (current >= stock) {
      toast.error(`${product?.name || "Product"}: max available stock is ${stock}`);
      return;
    }
    setCart({ ...cart, [id]: current + 1 });
  };
  const dec = (id) => setCart({ ...cart, [id]: Math.max(0, (cart[id] || 0) - 1) });

  const items = useMemo(() =>
    Object.entries(cart)
      .filter(([, q]) => q > 0)
      .map(([id, q]) => {
        const pr = products.find(x => x.id === id);
        const subtotal = Number(((pr?.price || 0) * q).toFixed(2));
        return { ...pr, quantity: q, subtotal };
      }),
    [cart, products]
  );
  const total = items.reduce((s, i) => s + i.subtotal, 0);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: p.business_name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Shop link copied"); }
    } catch { /* user cancelled */ }
  };

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
      <p className="text-red-700 font-semibold">{err}</p>
      <Link to="/directory" className="mt-4 text-emerald-800 hover:underline text-sm">← Back to directory</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading shop...</div>;

  const addr = [p.address, p.city, p.state, p.pincode].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8" data-testid="partner-shop-page">
      <header className="bg-emerald-950 text-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/directory" className="flex items-center gap-2 text-sm hover:text-amber-400" data-testid="back-to-directory">
            <ArrowLeft className="w-4 h-4" /> Directory
          </Link>
          <Logo />
        </div>
      </header>

      {/* SHOP HERO */}
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-white relative overflow-hidden">
        {p.banner_url ? (
          <div className="absolute inset-0 opacity-25">
            <img src={p.banner_url} alt="Shop banner" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-emerald-950/55" />
          </div>
        ) : null}
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-10 relative">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-amber-400 text-emerald-950 flex items-center justify-center shrink-0 overflow-hidden">
              {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-10 h-10" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold flex items-center gap-2">
                {p.partner_code} · Verified Partner
                {p.is_featured && (
                  <span className="inline-flex items-center gap-1 bg-amber-400 text-emerald-950 px-2 py-0.5 rounded-full text-[9px]">
                    <Star className="w-2.5 h-2.5 fill-emerald-950" /> Featured
                  </span>
                )}
              </p>
              <h1 className="font-display font-black text-2xl md:text-4xl mt-1" data-testid="shop-title">{p.business_name}</h1>
              <p className="text-emerald-100/80 mt-1 text-sm capitalize">{p.business_type}{p.contact_person ? ` · ${p.contact_person}` : ""}</p>
              {addr && (
                <p className="mt-3 flex items-start gap-1.5 text-xs text-emerald-100/90 max-w-2xl">
                  <MapPin className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <span data-testid="shop-address">{addr}</span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {p.phone && (
              <a
                href={`tel:${p.phone}`}
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-xs font-semibold px-4 py-2 transition"
                data-testid="shop-call"
              >
                <Phone className="w-3.5 h-3.5" /> {p.phone}
              </a>
            )}
            {waUrl(p) && (
              <a
                href={waUrl(p)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white border border-green-400 rounded-full text-xs font-bold px-4 py-2 transition"
                data-testid="shop-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            <a
              href={mapsUrl(p)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-xs font-semibold px-4 py-2 transition"
              data-testid="shop-address-chip"
            >
              <MapPin className="w-3.5 h-3.5" /> Address
            </a>
            <a
              href={mapsUrl(p)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-xs font-semibold px-4 py-2 transition"
              data-testid="shop-directions"
            >
              <Navigation className="w-3.5 h-3.5" /> Get Directions
            </a>
            <button
              onClick={share}
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-xs font-semibold px-4 py-2 transition"
              data-testid="shop-share"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </div>

      {!user && (
        <div className="max-w-6xl mx-auto px-4 pt-6">
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="guest-cta">
            <LogIn className="w-5 h-5 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-emerald-950 text-sm">Guest checkout is available</p>
              <p className="text-xs text-slate-700 mt-0.5">Buy without login. Add Member ID/Code at checkout only if you want reward percentage attribution.</p>
            </div>
            <Link to={`/login?next=/partner-shop/${partnerCode}`}>
              <Button size="sm" className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full">Sign In (Optional)</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" variant="outline" className="rounded-full border-emerald-900 text-emerald-900 hover:bg-emerald-50">Join Free</Button>
            </Link>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {featuredImages.length > 0 && (
          <div className="mb-6 bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Images className="w-4 h-4 text-emerald-700" />
              <h2 className="font-display font-bold text-xl text-emerald-950">Partner Featured Images</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              {featuredImages.map((img, index) => (
                <div key={`${img}-${index}`} className="aspect-square rounded-lg overflow-hidden border border-border bg-slate-100">
                  <img src={img} alt={`Featured ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-[1.2fr_1fr] mb-6">
          <div className="bg-white rounded-xl border border-border p-4">
            <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">METHO + {p.business_name}</p>
            <h3 className="font-display font-bold text-emerald-950 mt-1">Partner Image</h3>
            <div className="mt-3 aspect-[16/9] rounded-lg overflow-hidden bg-slate-100 border border-border">
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.business_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <Building2 className="w-10 h-10" />
                </div>
              )}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-border p-4 flex flex-col justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Full Catalog</p>
              <h3 className="font-display font-bold text-emerald-950 mt-1">Product Gallery</h3>
              <p className="text-xs text-slate-600 mt-2">সব products gallery page-এ দেখুন এবং সেখান থেকেও cart এ add করতে পারবেন।</p>
            </div>
            <Link to={`/gallery/${partnerCode}`} className="mt-4">
              <Button className="w-full bg-emerald-900 hover:bg-emerald-950 text-white rounded-full" data-testid="partner-gallery-btn">
                <Images className="w-4 h-4 mr-2" /> Product Gallery
              </Button>
            </Link>
          </div>
        </div>

        {/* Cashback banner */}
        {cashback?.percent > 0 && (user?.role !== "member" || cashback.eligible) && (
          <div className="mb-6 rounded-xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex flex-wrap items-center gap-3" data-testid="cashback-banner">
            <div className="w-10 h-10 rounded-full bg-amber-400 text-emerald-950 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-black text-emerald-950 text-sm md:text-base">
                {user?.role === "member" ? "First Partner-shop order?" : "New member offer"} Get {cashback.percent}% cashback
                {cashback.max > 0 ? <span className="text-amber-800"> (up to ₹{cashback.max})</span> : null}
              </p>
              <p className="text-xs text-slate-700 mt-0.5 font-body">
                Auto-credited to your Wallet once your first Partner order is approved. One-time offer per member.
              </p>
            </div>
            {!user && (
              <Link to={`/register`}>
                <Button size="sm" className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full">Join Free →</Button>
              </Link>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
          <h2 className="font-display font-bold text-xl text-emerald-950">Top 5 Products</h2>
          <span className="text-sm text-muted-foreground">({featuredProducts.length})</span>
          <span className="text-xs text-slate-500">of {products.length} total products</span>
        </div>

        {featuredProducts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-border">
            <p className="text-muted-foreground">This partner has not listed any products yet.</p>
            {p.phone && (
              <a href={`tel:${p.phone}`} className="inline-block mt-3 text-emerald-800 font-semibold text-sm hover:underline">
                Call {p.phone} for enquiries
              </a>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredProducts.map(pr => {
              const qty = cart[pr.id] || 0;
              const outOfStock = (pr.stock ?? 0) <= 0;
              return (
                <div key={pr.id} className="bg-white rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow" data-testid={`shop-product-${pr.id}`}>
                  <div className="aspect-square bg-secondary overflow-hidden">
                    {pr.image_url || placeholder
                      ? <img src={pr.image_url || placeholder} alt={pr.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-slate-400"><Building2 className="w-8 h-8" /></div>}
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">{pr.category}</p>
                    <p className="font-display font-bold text-emerald-950 text-sm line-clamp-1 mt-0.5">{pr.name}</p>
                    {pr.description && <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 font-body">{pr.description}</p>}
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-display font-black text-lg text-emerald-950">₹{pr.price}</span>
                      <span className={`text-[10px] font-semibold ${outOfStock ? "text-red-600" : "text-slate-500"}`}>
                        {outOfStock ? "Out of stock" : `Stock: ${pr.stock}`}
                      </span>
                    </div>
                    <div className="mt-3">
                      {outOfStock ? (
                        <Button size="sm" disabled className="w-full rounded-full text-xs">Unavailable</Button>
                      ) : qty > 0 ? (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-full px-2 py-1">
                          <button onClick={() => dec(pr.id)} className="w-7 h-7 rounded-full bg-white hover:bg-emerald-100 flex items-center justify-center" data-testid={`shop-dec-${pr.id}`}>
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="font-bold text-emerald-950 text-sm">{qty}</span>
                          <button onClick={() => inc(pr.id)} className="w-7 h-7 rounded-full bg-white hover:bg-emerald-100 flex items-center justify-center" data-testid={`shop-inc-${pr.id}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <Button onClick={() => inc(pr.id)} size="sm" className="w-full bg-emerald-900 hover:bg-emerald-950 rounded-full text-xs" data-testid={`shop-add-${pr.id}`}>
                          Add to Cart
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Sticky Cart Bar */}
      {items.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-emerald-950 text-white shadow-2xl border-t border-emerald-800" data-testid="sticky-cart-bar">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Cart</p>
              <p className="font-display font-bold text-lg leading-tight">{items.length} item{items.length !== 1 ? "s" : ""} · ₹{total.toLocaleString("en-IN")}</p>
              {!user && <p className="text-[11px] text-emerald-100/80">Guest checkout: reward percentage only if Member ID/Code is provided.</p>}
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="bg-amber-400 hover:bg-amber-500 text-emerald-950 rounded-full font-bold h-11 px-6"
              data-testid="shop-checkout"
            >
              <ShoppingCart className="w-4 h-4 mr-2" /> Checkout
            </Button>
          </div>
        </div>
      )}

      <UpiPaymentDialog
        open={open}
        onOpenChange={setOpen}
        items={items}
        total={total}
        paymentConfig={paymentProfile ? {
          upi_id: paymentProfile.upi_id,
          payee_name: paymentProfile.payee_name,
          qr_url: paymentProfile.qr_url,
          manual_upi_enabled: paymentProfile.manual_upi_enabled,
          razorpay_enabled: false,
          label: "Partner UPI Payment",
        } : null}
        isGuest={!user}
        memberRef={guestMemberRef}
        onMemberRefChange={setGuestMemberRef}
        onOrderPlaced={() => {
          setCart({});
          setGuestMemberRef("");
          setOpen(false);
          toast.success("Order placed successfully");
          if (user) setTimeout(() => nav("/app/orders"), 400);
        }}
      />
    </div>
  );
}

